import { useState, useEffect, useRef } from 'preact/hooks';
import { authFetch } from '../api/authFetch';
import { useReaderModeOptional } from '../hooks/useReaderMode';
import { ReaderToggle } from './ReaderMode/ReaderToggle';

// ─── Types ────────────────────────────────────────────────────────────────────

interface VocabularyItem {
  term: string;
  definition: string;
  exampleSentence: string;
}

interface QuestionItem {
  question: string;
  answer: string;
}

interface LessonPackage {
  id?: string;
  title: string;
  subject: string;
  difficulty?: string;
  language?: string;
  url?: string | null;
  vocabulary: VocabularyItem[];
  dictation: { text: string; wordCount: number };
  questions: QuestionItem[];
  articleText?: string;
  translatedText?: string;
  generatedAt?: string;
}

type Tab = 'vocabulary' | 'reading' | 'dictation' | 'questions';

const SUBJECTS = [
  { value: 'english', label: 'English' },
  { value: 'biology', label: 'Biology' },
  { value: 'history', label: 'History' },
  { value: 'geography', label: 'Geography' },
  { value: 'chemistry', label: 'Chemistry' },
  { value: 'physics', label: 'Physics' },
  { value: 'literature', label: 'Literature' },
  { value: 'any', label: 'General' },
];

const DIFFICULTIES = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
];

// BCP-47 lang codes for Web Speech API
const SPEECH_LANG: Record<string, string> = {
  en: 'en-US', bg: 'bg-BG', es: 'es-ES', fr: 'fr-FR', de: 'de-DE', it: 'it-IT', pt: 'pt-PT',
};

const LOAD_STEPS = [
  'Fetching article…',
  'Extracting content…',
  'Generating lesson with AI…',
  'Almost ready…',
];

const selectStyle = {
  flex: '1', minWidth: '90px', padding: '9px 10px', borderRadius: '8px',
  border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--fg)', fontSize: '14px',
};

const fieldLabelStyle = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  marginBottom: '4px',
};

const fieldWrapperStyle = { flex: '1 1 120px', minWidth: '100px' };

// ─── Web Speech TTS ────────────────────────────────────────────────────────────

function speak(text: string, lang: string, onEnd?: () => void) {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utt = new SpeechSynthesisUtterance(text);
  utt.lang = SPEECH_LANG[lang] ?? 'en-US';
  utt.rate = 0.85;
  if (onEnd) utt.onend = onEnd;
  window.speechSynthesis.speak(utt);
}

// ─── ReadingTab ───────────────────────────────────────────────────────────────

interface Tooltip { term: string; definition: string; x: number; y: number }

function ReadingTab({ lesson }: { lesson: LessonPackage }) {
  const [showTranslation, setShowTranslation] = useState(false);
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const reader = useReaderModeOptional();
  useEffect(() => {
    if (!reader) return;
    const el = containerRef.current;
    if (!el) return;
    return reader.registerSurface('lesson-reading', el);
  }, [reader]);

  const text = lesson.articleText || '';
  const translated = lesson.translatedText || '';
  const hasTranslation = translated.trim().length > 0 && lesson.language !== 'en';

  // Build vocab lookup (lowercase)
  const vocabMap = new Map<string, VocabularyItem>();
  for (const v of lesson.vocabulary) {
    vocabMap.set(v.term.toLowerCase(), v);
  }

  // Sort terms longest-first for greedy matching
  const sortedTerms = [...vocabMap.keys()].sort((a, b) => b.length - a.length);

  function tokenise(para: string): Array<{ text: string; vocab?: VocabularyItem }> {
    const result: Array<{ text: string; vocab?: VocabularyItem }> = [];
    let remaining = para;
    while (remaining.length > 0) {
      let matched = false;
      const lower = remaining.toLowerCase();
      for (const term of sortedTerms) {
        const idx = lower.indexOf(term);
        if (idx === 0) {
          result.push({ text: remaining.slice(0, term.length), vocab: vocabMap.get(term) });
          remaining = remaining.slice(term.length);
          matched = true;
          break;
        }
        if (idx > 0) {
          result.push({ text: remaining.slice(0, idx) });
          result.push({ text: remaining.slice(idx, idx + term.length), vocab: vocabMap.get(term) });
          remaining = remaining.slice(idx + term.length);
          matched = true;
          break;
        }
      }
      if (!matched) { result.push({ text: remaining }); remaining = ''; }
    }
    return result;
  }

  function handleVocabClick(e: MouseEvent, v: VocabularyItem) {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    setTooltip({
      term: v.term,
      definition: v.definition,
      x: rect.left - (containerRect?.left ?? 0),
      y: rect.bottom - (containerRect?.top ?? 0) + 4,
    });
  }

  const paragraphs = text.split(/\n\n+/).filter(p => p.trim());
  const translatedParas = translated.split(/\n\n+/).filter(p => p.trim());

  if (!text) {
    return (
      <div style={{ color: 'var(--fg-muted)', textAlign: 'center', padding: '32px 16px' }}>
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>📄</div>
        <div>Article text not available for this lesson.</div>
        <div style={{ fontSize: '12px', marginTop: '6px' }}>Generate a new lesson to see the reading view.</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            position: 'absolute', left: Math.min(tooltip.x, 200), top: tooltip.y,
            background: 'var(--fg)', color: 'var(--bg)', borderRadius: '8px',
            padding: '8px 12px', fontSize: '13px', maxWidth: '220px', zIndex: 50,
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          }}
          onClick={() => setTooltip(null)}
        >
          <div style={{ fontWeight: 700, marginBottom: '4px' }}>{tooltip.term}</div>
          <div style={{ lineHeight: '1.4' }}>{tooltip.definition}</div>
          <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.6 }}>Tap to dismiss</div>
        </div>
      )}

      {/* Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{ fontSize: '12px', color: 'var(--fg-muted)' }}>
          {lesson.vocabulary.length > 0 && (
            <span>Tap <span style={{ color: 'var(--accent)', fontWeight: 600 }}>highlighted</span> words for definitions</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <ReaderToggle surfaceId="lesson-reading" />
          {hasTranslation && (
            <button
              type="button"
              onClick={() => setShowTranslation(!showTranslation)}
              style={{
                padding: '5px 12px', borderRadius: '6px',
                border: `1px solid ${showTranslation ? 'var(--accent)' : 'var(--border)'}`,
                background: showTranslation ? 'color-mix(in srgb, var(--accent) 12%, var(--surface))' : 'var(--surface)',
                color: showTranslation ? 'var(--accent)' : 'var(--fg-muted)',
                fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              {showTranslation ? '🌐 Hide translation' : `🌐 Show ${LANGUAGES.find(l => l.value === lesson.language)?.label ?? ''}`}
            </button>
          )}
        </div>
      </div>

      {/* Article paragraphs */}
      <div onClick={() => setTooltip(null)}>
        {paragraphs.map((para, i) => (
          <div key={i} style={{ marginBottom: '16px' }} data-reader-block-id={`para-${i}`}>
            <p style={{ margin: 0, lineHeight: '1.85', fontSize: '15px' }}>
              {tokenise(para).map((tok, j) =>
                tok.vocab ? (
                  <span
                    key={j}
                    onClick={(e) => { e.stopPropagation(); handleVocabClick(e, tok.vocab!); }}
                    style={{
                      color: 'var(--accent)', textDecoration: 'underline dotted',
                      cursor: 'pointer', fontWeight: 500,
                    }}
                  >
                    {tok.text}
                  </span>
                ) : (
                  <span key={j}>{tok.text}</span>
                )
              )}
            </p>
            {showTranslation && translatedParas[i] && (
              <p style={{
                margin: '8px 0 0', lineHeight: '1.7', fontSize: '14px',
                color: 'var(--fg-muted)', borderLeft: '3px solid var(--accent)',
                paddingLeft: '10px', fontStyle: 'italic',
              }}>
                {translatedParas[i]}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── Main component ───────────────────────────────────────────────────────────

interface LessonsPageProps { lessonId?: string }

export function LessonsPage({ lessonId }: LessonsPageProps) {
  const [inputMode, setInputMode] = useState<'url' | 'text'>('url');
  const [url, setUrl] = useState('');
  const [pastedText, setPastedText] = useState('');
  const [pastedTitle, setPastedTitle] = useState('');
  const [subject, setSubject] = useState('english');
  const [difficulty, setDifficulty] = useState('intermediate');
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(false);
  const [loadStep, setLoadStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [lesson, setLesson] = useState<LessonPackage | null>(null);
  const [tab, setTab] = useState<Tab>('vocabulary');
  const [lessonHistory, setLessonHistory] = useState<LessonPackage[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [showDictation, setShowDictation] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [expandedQ, setExpandedQ] = useState<number | null>(null);
  const [copyToast, setCopyToast] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    loadHistory();
    if (lessonId) loadLesson(lessonId);
  }, [lessonId]);

  useEffect(() => {
    if (!loading) { setLoadStep(0); return; }
    const id = setInterval(() => setLoadStep(s => Math.min(s + 1, LOAD_STEPS.length - 1)), 4000);
    return () => clearInterval(id);
  }, [loading]);

  // Cancel TTS when unmounting or switching tabs
  useEffect(() => {
    return () => { if ('speechSynthesis' in window) window.speechSynthesis.cancel(); };
  }, []);

  async function loadLesson(id: string) {
    setLoading(true); setError(null);
    try {
      const res = await authFetch(`/api/lessons/${id}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as LessonPackage;
      setLesson(data); setTab('vocabulary'); resetTabState();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load lesson');
    } finally { setLoading(false); }
  }

  async function loadHistory() {
    setHistoryLoading(true);
    try {
      const res = await authFetch('/api/lessons/history');
      if (res.ok) setLessonHistory(await res.json() as LessonPackage[]);
    } catch { /* non-fatal */ }
    finally { setHistoryLoading(false); }
  }

  function resetTabState() {
    setShowDictation(false); setShowAnswers(false);
    setExpandedQ(null); setSpeaking(false);
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  }

  const canSubmit = inputMode === 'url'
    ? url.trim().length > 0
    : pastedText.trim().length >= 200;

  async function handleGenerate(e: Event) {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true); setError(null); setLesson(null);
    try {
      const body = inputMode === 'url'
        ? { url: url.trim(), subject, difficulty, language }
        : { text: pastedText.trim(), title: pastedTitle.trim() || undefined, subject, difficulty, language };
      const res = await authFetch('/api/lessons/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ message: 'Unknown error' })) as { message?: string };
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      const data = await res.json() as LessonPackage;
      setLesson(data); setTab('vocabulary'); resetTabState();
      if (data.id) window.history.pushState(null, '', `/lessons/${data.id}`);
      setLessonHistory(prev => [data, ...prev.filter(h => h.id !== data.id).slice(0, 19)]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to generate lesson');
    } finally { setLoading(false); }
  }

  function loadFromHistory(item: LessonPackage) {
    setLesson(item); setTab('vocabulary'); resetTabState(); setError(null);
    if (item.id) window.history.pushState(null, '', `/lessons/${item.id}`);
  }

  function startNewLesson() {
    setLesson(null); setError(null);
    window.history.pushState(null, '', '/lessons');
    resetTabState();
  }

  async function pasteFromClipboard() {
    try { const t = await navigator.clipboard.readText(); if (t.trim()) setUrl(t.trim()); } catch { /* denied */ }
  }

  async function copyLink() {
    try { await navigator.clipboard.writeText(window.location.href); setCopyToast(true); setTimeout(() => setCopyToast(false), 2000); } catch { /* ignore */ }
  }

  function handleBack() {
    if (lessonId) window.location.href = '/lessons';
    else window.history.back();
  }

  function handleSpeak() {
    if (!lesson) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    setSpeaking(true);
    speak(lesson.dictation.text, lesson.language ?? 'en', () => setSpeaking(false));
  }

  // Tab labels
  const TABS: { id: Tab; label: string }[] = [
    { id: 'vocabulary', label: 'Words' },
    { id: 'reading', label: 'Reading' },
    { id: 'dictation', label: 'Dictation' },
    { id: 'questions', label: 'Q&A' },
  ];

  function HistoryList({ compact }: { compact?: boolean }) {
    return (
      <div style={{ marginTop: compact ? '28px' : '0' }}>
        <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '10px', color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Recent Lessons
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {lessonHistory.map(item => {
            const isActive = lesson?.id === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => loadFromHistory(item)}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                  padding: '10px 12px', borderRadius: '8px',
                  background: isActive ? 'color-mix(in srgb, var(--accent) 12%, var(--surface))' : 'var(--surface)',
                  border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
                  cursor: 'pointer', textAlign: 'left', width: '100%', color: 'var(--fg)',
                }}
              >
                <span style={{ fontWeight: 500, fontSize: '14px', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                  {isActive && <span style={{ color: 'var(--accent)', marginRight: '6px' }}>▶</span>}
                  {!item.url && <span title="Pasted text" style={{ marginRight: '6px' }}>📝</span>}
                  {item.title}
                </span>
                <span style={{ fontSize: '12px', color: 'var(--fg-muted)', textTransform: 'capitalize' }}>
                  {item.subject}{item.difficulty && ` · ${item.difficulty}`}{item.generatedAt && ` · ${new Date(item.generatedAt).toLocaleDateString()}`}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes lessons-spin { to { transform: rotate(360deg); } }
        .lessons-spinner {
          display: inline-block; width: 28px; height: 28px;
          border: 3px solid var(--border); border-top-color: var(--accent);
          border-radius: 50%; animation: lessons-spin 0.8s linear infinite;
        }
        .lessons-tabs { display: flex; overflow-x: auto; scrollbar-width: none; border-bottom: 2px solid var(--border); margin-bottom: 16px; -webkit-overflow-scrolling: touch; }
        .lessons-tabs::-webkit-scrollbar { display: none; }
        .lessons-tab-btn {
          padding: 11px 14px; background: none; border: none; cursor: pointer;
          border-bottom: 2px solid transparent; margin-bottom: -2px;
          font-size: 13px; white-space: nowrap; min-height: 44px; flex-shrink: 0;
        }
      `}</style>

      {/* Full-height scroll container — owns its own scroll to avoid body overflow:hidden issues */}
      <div style={{ height: '100dvh', overflow: 'auto', background: 'var(--bg)', color: 'var(--fg)', fontFamily: 'inherit', display: 'flex', flexDirection: 'column' }}>

        {/* Sticky header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '12px 16px', borderBottom: '1px solid var(--border)',
          background: 'var(--surface)', position: 'sticky', top: 0, zIndex: 10,
          flexShrink: 0,
        }}>
          <button type="button" onClick={handleBack}
            style={{ background: 'none', border: 'none', color: 'var(--fg)', fontSize: '18px', cursor: 'pointer', padding: '4px 8px 4px 0', minHeight: '44px' }}>
            ←
          </button>
          <span style={{ fontWeight: 600, fontSize: '17px', flex: 1 }}>Lesson Generator</span>
          {lesson && (
            <button type="button" onClick={startNewLesson}
              style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--fg)', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              + New
            </button>
          )}
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px', maxWidth: '700px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>

          {/* Form */}
          {!lesson && (
            <form onSubmit={handleGenerate} style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
              {/* Mode toggle */}
              <div role="tablist" aria-label="Lesson source" style={{
                display: 'flex', gap: '4px', padding: '3px', borderRadius: '10px',
                border: '1px solid var(--border)', background: 'var(--surface)',
              }}>
                {(['url', 'text'] as const).map(m => {
                  const active = inputMode === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setInputMode(m)}
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: '8px', border: 'none',
                        background: active ? 'var(--accent)' : 'transparent',
                        color: active ? '#fff' : 'var(--fg-muted)',
                        fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      {m === 'url' ? '🔗 URL' : '📝 Paste text'}
                    </button>
                  );
                })}
              </div>

              {inputMode === 'url' ? (
                <label style={{ display: 'block' }}>
                  <span style={fieldLabelStyle}>Article URL</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text" value={url}
                      onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
                      placeholder="https://example.com/article"
                      style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--fg)', fontSize: '15px', minWidth: 0 }}
                    />
                    <button type="button" onClick={pasteFromClipboard} title="Paste URL from clipboard" aria-label="Paste URL from clipboard"
                      style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--fg-muted)', fontSize: '16px', cursor: 'pointer', flexShrink: 0 }}>
                      📋
                    </button>
                  </div>
                </label>
              ) : (
                <>
                  <label style={{ display: 'block' }}>
                    <span style={fieldLabelStyle}>Title (optional)</span>
                    <input
                      type="text" value={pastedTitle}
                      onInput={(e) => setPastedTitle((e.target as HTMLInputElement).value)}
                      placeholder="Leave blank to derive from the first line"
                      maxLength={200}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--fg)', fontSize: '15px' }}
                    />
                  </label>
                  <label style={{ display: 'block' }}>
                    <span style={fieldLabelStyle}>
                      Article text
                      <span style={{ fontWeight: 400, marginLeft: '6px', color: pastedText.trim().length >= 200 ? 'var(--fg-muted)' : 'var(--accent)' }}>
                        ({pastedText.trim().length.toLocaleString()} / 200 min)
                      </span>
                    </span>
                    <textarea
                      value={pastedText}
                      onInput={(e) => setPastedText((e.target as HTMLTextAreaElement).value)}
                      placeholder="Paste article text here…"
                      rows={8}
                      maxLength={50000}
                      style={{
                        width: '100%', boxSizing: 'border-box', padding: '10px 12px',
                        borderRadius: '8px', border: '1px solid var(--border)',
                        background: 'var(--surface)', color: 'var(--fg)', fontSize: '14px',
                        fontFamily: 'inherit', lineHeight: 1.5, resize: 'vertical', minHeight: '160px',
                      }}
                    />
                  </label>
                </>
              )}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <label style={fieldWrapperStyle}>
                  <span style={fieldLabelStyle}>Subject</span>
                  <select value={subject} onChange={(e) => setSubject((e.target as HTMLSelectElement).value)} style={{ ...selectStyle, width: '100%' }}>
                    {SUBJECTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </label>
                <label style={fieldWrapperStyle}>
                  <span style={fieldLabelStyle}>Language</span>
                  <select value={language} onChange={(e) => setLanguage((e.target as HTMLSelectElement).value)} style={{ ...selectStyle, width: '100%' }}>
                    {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </label>
                <label style={fieldWrapperStyle}>
                  <span style={fieldLabelStyle}>Difficulty</span>
                  <select value={difficulty} onChange={(e) => setDifficulty((e.target as HTMLSelectElement).value)} style={{ ...selectStyle, width: '100%' }}>
                    {DIFFICULTIES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </label>
              </div>
              <button type="submit" disabled={loading || !canSubmit}
                style={{ padding: '11px 20px', borderRadius: '8px', border: 'none', background: loading || !canSubmit ? 'var(--border)' : 'var(--accent)', color: '#fff', fontWeight: 600, fontSize: '15px', cursor: loading || !canSubmit ? 'not-allowed' : 'pointer' }}>
                Generate Lesson
              </button>
            </form>
          )}

          {/* Error */}
          {error && (
            <div style={{ padding: '12px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--fg)', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '16px', flexShrink: 0 }}>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--fg-muted)' }}>
              <div class="lessons-spinner" style={{ margin: '0 auto 16px' }} />
              <div style={{ fontSize: '15px', fontWeight: 500, color: 'var(--fg)', marginBottom: '6px' }}>{LOAD_STEPS[loadStep]}</div>
              <div style={{ fontSize: '12px', opacity: 0.6 }}>15–25 seconds</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '16px' }}>
                {LOAD_STEPS.map((_, i) => (
                  <div key={i} style={{ width: '6px', height: '6px', borderRadius: '50%', background: i === loadStep ? 'var(--accent)' : 'var(--border)', transition: 'background 0.3s' }} />
                ))}
              </div>
            </div>
          )}

          {/* Empty / history state */}
          {!loading && !lesson && (
            <>
              {!historyLoading && lessonHistory.length === 0 && (
                <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--fg-muted)' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>📚</div>
                  <div style={{ fontSize: '15px', fontWeight: 500, marginBottom: '8px', color: 'var(--fg)' }}>Turn any article into a lesson</div>
                  <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                    Paste a URL or raw article text above.<br />
                    Generates vocabulary, reading view, dictation,<br />
                    and comprehension Q&A.
                  </div>
                </div>
              )}
              {lessonHistory.length > 0 && <HistoryList />}
            </>
          )}

          {/* Lesson result */}
          {lesson && !loading && (
            <div>
              {/* Lesson header */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '17px', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {lesson.title}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--fg-muted)', textTransform: 'capitalize' }}>
                      {lesson.subject}{lesson.difficulty && ` · ${lesson.difficulty}`}
                      {lesson.language && lesson.language !== 'en' && ` · ${LANGUAGES.find(l => l.value === lesson.language)?.label ?? lesson.language}`}
                    </div>
                  </div>
                  <button type="button" onClick={copyLink} title="Copy link"
                    style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--border)', background: copyToast ? 'var(--accent)' : 'var(--surface)', color: copyToast ? '#fff' : 'var(--fg-muted)', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0 }}>
                    {copyToast ? '✓ Copied' : '🔗'}
                  </button>
                </div>
              </div>

              {/* Tabs — horizontally scrollable */}
              <div class="lessons-tabs">
                {TABS.map(t => (
                  <button
                    key={t.id} type="button"
                    class="lessons-tab-btn"
                    onClick={() => setTab(t.id)}
                    style={{ color: tab === t.id ? 'var(--accent)' : 'var(--fg-muted)', borderBottomColor: tab === t.id ? 'var(--accent)' : 'transparent', fontWeight: tab === t.id ? 600 : 400 }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Vocabulary tab */}
              {tab === 'vocabulary' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {lesson.vocabulary.length === 0
                    ? <div style={{ color: 'var(--fg-muted)', textAlign: 'center', padding: '20px' }}>No vocabulary generated.</div>
                    : lesson.vocabulary.map((v, i) => (
                      <div key={i} style={{ padding: '12px', borderRadius: '8px', background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <div style={{ fontWeight: 600, color: 'var(--accent)', marginBottom: '4px' }}>{v.term}</div>
                        <div style={{ fontSize: '14px', marginBottom: '4px' }}>{v.definition}</div>
                        <div style={{ fontSize: '13px', color: 'var(--fg-muted)', fontStyle: 'italic' }}>"{v.exampleSentence}"</div>
                      </div>
                    ))
                  }
                </div>
              )}

              {/* Reading tab */}
              {tab === 'reading' && <ReadingTab lesson={lesson} />}

              {/* Dictation tab */}
              {tab === 'dictation' && (
                <div style={{ padding: '16px', borderRadius: '8px', background: 'var(--surface)', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>Dictation Passage</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--fg-muted)' }}>{lesson.dictation.wordCount} words</span>
                      {'speechSynthesis' in window && (
                        <button type="button" onClick={handleSpeak}
                          style={{ padding: '5px 10px', borderRadius: '6px', border: `1px solid ${speaking ? 'var(--accent)' : 'var(--border)'}`, background: speaking ? 'color-mix(in srgb, var(--accent) 12%, var(--surface))' : 'var(--bg)', color: speaking ? 'var(--accent)' : 'var(--fg)', fontSize: '13px', cursor: 'pointer' }}>
                          {speaking ? '⏹ Stop' : '🔊 Listen'}
                        </button>
                      )}
                      <button type="button" onClick={() => setShowDictation(!showDictation)}
                        style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--fg)', fontSize: '12px', cursor: 'pointer' }}>
                        {showDictation ? 'Hide' : 'Reveal'}
                      </button>
                    </div>
                  </div>
                  {showDictation
                    ? <div style={{ fontSize: '15px', lineHeight: '1.7', letterSpacing: '0.01em' }}>{lesson.dictation.text}</div>
                    : (
                      <div style={{ padding: '24px', textAlign: 'center', borderRadius: '6px', background: 'var(--bg)', border: '1px dashed var(--border)', color: 'var(--fg-muted)', fontSize: '14px' }}>
                        Passage hidden for dictation practice.<br />
                        <span style={{ fontSize: '12px' }}>
                          {('speechSynthesis' in window) ? 'Press 🔊 Listen to hear it, then write what you hear.' : 'Tap Reveal when ready to check.'}
                        </span>
                      </div>
                    )
                  }
                </div>
              )}

              {/* Q&A tab */}
              {tab === 'questions' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                    <button type="button"
                      onClick={() => { setShowAnswers(!showAnswers); setExpandedQ(null); }}
                      style={{ padding: '7px 14px', borderRadius: '6px', border: '1px solid var(--border)', background: showAnswers ? 'var(--accent)' : 'var(--surface)', color: showAnswers ? '#fff' : 'var(--fg)', fontSize: '13px', cursor: 'pointer' }}>
                      {showAnswers ? 'Hide All' : 'Reveal All'}
                    </button>
                  </div>
                  {lesson.questions.length === 0
                    ? <div style={{ color: 'var(--fg-muted)', textAlign: 'center', padding: '20px' }}>No questions generated.</div>
                    : lesson.questions.map((q, i) => (
                      <div key={i} style={{ padding: '12px', borderRadius: '8px', background: 'var(--surface)', border: '1px solid var(--border)', marginBottom: '8px' }}>
                        <div style={{ fontWeight: 600, fontSize: '14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', minHeight: '44px', alignItems: 'center' }}
                          onClick={() => setExpandedQ(expandedQ === i ? null : i)}>
                          <span style={{ flex: 1 }}>{i + 1}. {q.question}</span>
                          <span style={{ color: 'var(--fg-muted)', marginLeft: '8px', fontSize: '12px' }}>
                            {(showAnswers || expandedQ === i) ? '▲' : '▼'}
                          </span>
                        </div>
                        {(showAnswers || expandedQ === i) && (
                          <div style={{ marginTop: '8px', fontSize: '14px', color: 'var(--fg-muted)', borderTop: '1px solid var(--border)', paddingTop: '8px' }}>
                            {q.answer}
                          </div>
                        )}
                      </div>
                    ))
                  }
                </div>
              )}

              {lessonHistory.length > 0 && <HistoryList compact />}
            </div>
          )}

        </div>
      </div>
    </>
  );
}
